// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { IconButton, SecondaryButton, StatusBadge, XIcon } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { useAdminAutomationRuns } from "../../hooks/useAdminAutomations";
import RunFunnel, { runToFunnelCounts } from "./RunFunnel";
import { formatDateTime, formatRelativeTime, formatCount, getRunStatusMeta } from "./automationUtils";

const RUNS_LIMIT = 50;

const RunStatusBadge = ({ run }) => {
  const meta = getRunStatusMeta(run?.status);
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <StatusBadge size="sm" variant={meta.tone}>
        {meta.label}
      </StatusBadge>
      {run?.dry_run ? (
        <StatusBadge size="sm" variant="neutral" title="Dry run — the funnel was evaluated but nothing was enqueued">
          Dry run
        </StatusBadge>
      ) : null}
    </span>
  );
};

RunStatusBadge.propTypes = { run: PropTypes.object };

const RunDuration = ({ run }) => {
  if (!run?.started_at || !run?.finished_at) return <span className={textColors.muted}>—</span>;
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return <span className={textColors.muted}>—</span>;
  if (ms < 1000) return <>{ms} ms</>;
  if (ms < 60 * 1000) return <>{(ms / 1000).toFixed(1)} s</>;
  return <>{Math.round(ms / 60000)} min</>;
};

RunDuration.propTypes = { run: PropTypes.object };

/**
 * Slide-over panel listing the run history of one automation: started,
 * status (+ dry-run badge), the funnel counts and any error.
 */
const AutomationRunsDrawer = ({ isOpen, automation, onClose }) => {
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);
  const automationId = automation?.id ?? null;
  const runsQuery = useAdminAutomationRuns(automationId, { limit: RUNS_LIMIT, enabled: isOpen && automationId != null });
  const runs = Array.isArray(runsQuery.data?.results) ? runsQuery.data.results : [];

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );

  // Focus management + body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    previousActiveElement.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = setTimeout(() => {
      const button = closeButtonRef.current?.querySelector?.("button");
      button?.focus?.();
    }, 50);
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
      if (previousActiveElement.current && typeof previousActiveElement.current.focus === "function") {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen]);

  if (typeof document === "undefined") return null;

  const content = (
    <AnimatePresence>
      {isOpen ? (
        <div className="fixed inset-0 z-[5000]" onKeyDown={handleKeyDown}>
          <motion.div
            className="absolute inset-0 bg-dark-950/50 dark:bg-dark-950/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="automation-runs-title"
            className={`absolute inset-y-0 right-0 w-full sm:max-w-2xl flex flex-col ${surfaces.elevated} border-l ${borders.glass} shadow-2xl`}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
          >
            <div className={`flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b ${borders.glass} shrink-0`}>
              <div className="min-w-0">
                <h2 id="automation-runs-title" className={`${typography.h4} ${textColors.primary} truncate`}>
                  Run history
                </h2>
                <p className={`${typography.bodySmall} ${textColors.secondary} truncate`} title={automation?.name || ""}>
                  {automation?.name || "—"}
                  {automation?.slug ? <span className={`${textColors.muted} font-mono text-xs ml-2`}>{automation.slug}</span> : null}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <SecondaryButton size="sm" onClick={() => runsQuery.refetch()} disabled={runsQuery.isFetching}>
                  {runsQuery.isFetching ? "Refreshing…" : "Refresh"}
                </SecondaryButton>
                <span ref={closeButtonRef} className="inline-flex">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<XIcon size="sm" />}
                    aria-label="Close run history"
                    title="Close"
                    onClick={onClose}
                  />
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              <p className={`${typography.caption} ${textColors.tertiary} mb-3`}>
                Newest first · last {RUNS_LIMIT} runs. Dry runs evaluate the funnel without enqueuing anything.
              </p>

              {runsQuery.isLoading ? (
                <div className="space-y-3" aria-busy="true" aria-live="polite">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className={`rounded-2xl border ${borders.default} ${surfaces.primary} p-4`}>
                      <div className="animate-pulse space-y-2">
                        <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
                        <div className={`h-3 ${surfaces.tertiary} rounded w-3/4`} />
                        <div className={`h-3 ${surfaces.tertiary} rounded w-1/3`} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : runsQuery.error ? (
                <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
                  <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
                    Failed to load runs: {runsQuery.error?.message || "Unknown error"}
                  </p>
                  <div className="mt-2">
                    <SecondaryButton size="sm" onClick={() => runsQuery.refetch()}>
                      Retry
                    </SecondaryButton>
                  </div>
                </div>
              ) : runs.length === 0 ? (
                <div className={`rounded-2xl border border-dashed ${borders.default} ${surfaces.secondary} px-4 py-8 text-center`}>
                  <h3 className={`${typography.h5} ${textColors.primary}`}>No runs yet</h3>
                  <p className={`mt-1 ${typography.bodySmall} ${textColors.tertiary}`}>
                    Use “Run now” from the list to record a first run (drafts get a dry run).
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile: cards */}
                  <ol className="space-y-3 sm:hidden">
                    {runs.map((run) => (
                      <li key={run.id} className={`rounded-2xl border ${borders.default} ${surfaces.primary} p-4`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                              {formatRelativeTime(run.started_at)}
                            </div>
                            <div className={`${typography.caption} ${textColors.tertiary}`}>{formatDateTime(run.started_at)}</div>
                          </div>
                          <RunStatusBadge run={run} />
                        </div>
                        <div className="mt-3">
                          <RunFunnel counts={runToFunnelCounts(run)} mode="run" variant="full" />
                        </div>
                        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
                          Duration: <RunDuration run={run} />
                        </div>
                        {run.error ? (
                          <p className={`mt-2 ${typography.caption} text-red-700 dark:text-red-300 break-words`}>{run.error}</p>
                        ) : null}
                      </li>
                    ))}
                  </ol>

                  {/* Tablet+: table */}
                  <div className={`hidden sm:block rounded-2xl border ${borders.default} overflow-hidden`}>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left" aria-label="Runs">
                        <thead className={`${surfaces.secondary} border-b ${borders.default}`}>
                          <tr className={`${typography.caption} ${textColors.secondary}`}>
                            <th className="px-3 py-2 font-medium">Started</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium whitespace-nowrap" title="matched → entered → guards passed → enqueued">
                              Funnel
                            </th>
                            <th className="px-3 py-2 font-medium text-right">Suppressed</th>
                            <th className="px-3 py-2 font-medium">Duration</th>
                            <th className="px-3 py-2 font-medium">Error</th>
                          </tr>
                        </thead>
                        <tbody className={surfaces.primary}>
                          {runs.map((run) => (
                            <tr key={run.id} className={`border-t ${borders.light} align-top`}>
                              <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.primary} whitespace-nowrap`}>
                                <div>{formatRelativeTime(run.started_at)}</div>
                                <div className={`${typography.caption} ${textColors.tertiary}`}>{formatDateTime(run.started_at)}</div>
                              </td>
                              <td className="px-3 py-2">
                                <RunStatusBadge run={run} />
                              </td>
                              <td className="px-3 py-2">
                                <RunFunnel counts={runToFunnelCounts(run)} mode="run" variant="compact" />
                              </td>
                              <td className={`px-3 py-2 ${typography.bodySmall} text-right tabular-nums`}>
                                {Number(run.policy_suppressed_count || 0) > 0 ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                    {formatCount(run.policy_suppressed_count)}
                                  </span>
                                ) : (
                                  <span className={textColors.muted}>0</span>
                                )}
                              </td>
                              <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                                <RunDuration run={run} />
                              </td>
                              <td className={`px-3 py-2 ${typography.caption} max-w-[240px]`}>
                                {run.error ? (
                                  <span className="text-red-700 dark:text-red-300 break-words" title={run.error}>
                                    {run.error}
                                  </span>
                                ) : (
                                  <span className={textColors.muted}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.aside>
        </div>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

AutomationRunsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  automation: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default AutomationRunsDrawer;
